import axios from "axios";
import { getApiBaseUrl } from "../utils/apiConfig";

/**
 * Fetches the expertises for a given guide.
 * @param {number} guideId - The ID of the guide.
 * @returns {Promise<Array<Object>>} A promise that resolves to an array of expertise objects.
 */
export const fetchGuideExpertise = async (guideId) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/guide/${guideId}/expertises`);
    if (!response.ok) {
      throw new Error(`Failed to fetch expertises: ${response.statusText}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching guide expertise:", error);
    throw error;
  }
};

/**
 * Fetches the complete details for a guide and their associated tour.
 * @param {number} guideId - The ID of the guide listing.
 * @returns {Promise<Object>} A promise that resolves to the guide and tour data.
 */
export const fetchGuideAndTourDetails = async (guideId) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/guide-listing/tour/${guideId}`);
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error ||
          `Failed to load guide details: ${response.statusText}`
      );
    }
    const data = await response.json();
    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.error || "Failed to load guide details.");
    }
  } catch (error) {
    console.error("Error fetching guide and tour details:", error);
    throw error;
  }
};

export const getTourListings = async () => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await axios.get(
      `${baseUrl}/guide-listing/guide/tour/tour-listings`
    );
    return response.data.data;
  } catch (error) {
    console.error("Error fetching tour listings:", error);
    throw error;
  }
};

// Get images for one tour listing
export const getTourImages = async (tourListingId) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await axios.get(
      `${baseUrl}/guide-listing/${tourListingId}/images`
    );
    return response.data.images;
  } catch (error) {
    console.error(`Error fetching images for tour ${tourListingId}:`, error);
    return { images: [] };
  }
};

//  Create new tour listing
export const createTourListing = async (formData) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await axios.post(
      `${baseUrl}/guide-listing/create`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error creating tour listing:", error);
    throw error;
  }
};
